import { AddPointCustomizationComponent, CreateTopicRequest, CustomFieldRatingType, CustomFieldUrlType, FeatureType, TopicCategory, UserTag } from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { isDefined, isValidString, undefinedIfEmpty } from '../../../helpers/utils';

import { FormSwitch } from '../../theme/form-switch';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import { getMainPage } from '../../../helpers/page-name';
import { isValidImageUrl } from '../../../helpers/api/image-api';
import { logWithAmplitude } from '../../../helpers/api/api';
import topicData from '../../../helpers/state/topic-data';

@customElement('add-point-customization-element')
export class addPointCustomization extends LitElement {
    amplitude_callsite: string = 'Add Point Customization';

    @query('#isCheckedShowOnForm')
    isCheckedShowOnFormElement?: FormSwitch;

    @query('#isCheckedShowOnCard')
    isCheckedShowOnCardElement?: FormSwitch;

    @query('#isCheckedRequired')
    isCheckedRequiredElement?: FormSwitch;

    @query('#isCheckedShowAsButton')
    isCheckedShowAsButtonElement?: FormSwitch;

    @query('#isCheckedOpenInNewTab')
    isCheckedOpenInNewTabElement?: FormSwitch;

    @query('#textElementButtonColor')
    textElementButtonColorElement?: HTMLInputElement;

    @query('#textElementButtonIconUrl')
    textElementButtonIconUrlElement?: HTMLInputElement;

    @query('#textElementButtonName')
    textElementButtonNameElement?: HTMLInputElement;

    @property()
    field_name_toggle?: string;

    @property()
    field_name_text?: string;

    @property()
    toggle_description?: string;

    @property()
    field_name_color_picker?: string;

    @property()
    text_description?: string;

    @property({ type: Number })
    text_char_limit?: number = 30;

    @property()
    checked_field?: keyof CreateTopicRequest;

    @property()
    feature_type?: FeatureType;

    @property()
    title: string = '';

    @property()
    description?: string;

    @property()
    upgradeImg?: string;

    @state()
    initial_value_toggle?: boolean;

    @state()
    is_hidden: boolean = false;

    @state()
    hasEntitlement: boolean = false;

    @state()
    inputData: AddPointCustomizationComponent = {
        customization: '',
        componentTitle: '',
        hasHideOnForm: false,
        hasHideOnPointCard: false,
        hasRequired: false,
        hasTitle: false,
        hasDescription: false,
        hideOnForm: false,
        hideOnPointCard: false,
        required: false,
        title: '',
        description: '',
        userHasEntitlement: false
    };

    @state()
    isCustomField: boolean = false;

    @state()
    tags: UserTag[] = [];

	private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    firstUpdated() {
        // check if user has access to this setting
        if (this.inputData.userHasEntitlement) {
            this.hasEntitlement = true;
        } else if (!isDefined(this.inputData.userHasEntitlement)) {
            this.hasEntitlement = true;
        } else {
            this.getEntitlementIcon();
        }

        this.title = this.inputData.componentTitle;
        this.description = this.inputData.description;
        this.is_hidden = this.inputData.hideOnForm ?? false;

        // set isCheckedShowOnForm from input data

        if (this.inputData.hasHideOnForm && this.isCheckedShowOnFormElement) {
            this.isCheckedShowOnFormElement.checked = !(this.inputData.hideOnForm ?? true);
        }

        // set isCheckedShowOnCard from input data
        if (this.inputData.hasHideOnPointCard && this.isCheckedShowOnCardElement) {
            this.isCheckedShowOnCardElement.checked = !(this.inputData.hideOnPointCard ?? true);
        }

        // set isCheckedRequired from input data
        if (this.inputData.hasRequired && this.isCheckedRequiredElement) {
            this.isCheckedRequiredElement.checked = this.inputData.required ?? false;
        }

        if (this.inputData.customType && 'URL' in this.inputData.customType) {
            const urlType: CustomFieldUrlType = this.inputData.customType.URL ;
            if (this.isCheckedShowAsButtonElement) {
                this.isCheckedShowAsButtonElement.checked = urlType.show_as_button ?? false;
            }

            if (this.textElementButtonIconUrlElement) {
                this.textElementButtonIconUrlElement.value = urlType.button_icon_url ?? 'https://icons8.com/icon/60664/external-link';
            }

            if (this.textElementButtonColorElement) {
                this.textElementButtonColorElement.value = urlType.button_color ?? '#0000FF';
            }

            if (this.isCheckedOpenInNewTabElement) {
                this.isCheckedOpenInNewTabElement.checked = urlType.open_in_new_tab ?? true;
            }

            if (this.textElementButtonNameElement) {
                this.textElementButtonNameElement.value = urlType.button_text ?? 'Visit Link';
            }
        }

        if (this.inputData.customization === 'tags') {
            this.tags = topicData.allUserTags ?? [];
            // move the tags that are not crowdsourcable to the end
            this.tags.sort((a, b) => {
                if ((a.can_crowdsource ?? true) && !(b.can_crowdsource ?? true)) {
                    return -1;
                } else if (!(a.can_crowdsource ?? true) && (b.can_crowdsource ?? true)) {
                    return 1;
                } else {
                    return 0;
                }
            });
        }

        if (this.inputData.customization === 'custom') {
            this.isCustomField = true;
        }
    }

    get categories() : TopicCategory[] {
        return topicData.topic?.categories ?? [];
    }

    // handle change in hide on form
    private async settingIsChanged(e: Event, type: string, settingType: 'toggle' | 'text') {

        if (settingType === 'toggle') {
            const isChecked: boolean = (e as CustomEvent).detail;
            if (!isChecked && type === 'hide') {
                this.is_hidden = true;
            } else if (isChecked && type === 'hide') {
                this.is_hidden = false;
            }

            const field = this.inputData.customization;
            // if type contains "hide" then do the opposite of isChecked
            const finalValue = type.includes('hide') ? !isChecked : isChecked;

            this.dispatchEvent(
                new CustomEvent('on-toggle-subfield', {
                    detail: {
                        field,
                        subField: type,
                        updatedValue: finalValue
                    }
                })
            );
            logWithAmplitude(this.amplitude_callsite, field, { value: finalValue, type });

            // when a field is hidden from the form, clear `mandatory` so the two
            // flags can't drift — otherwise validation can demand a hidden field.
            if (type === 'hide' && finalValue && this.inputData.required === true) {
                this.inputData.required = false;
                this.dispatchEvent(
                    new CustomEvent('on-toggle-subfield', {
                        detail: {
                            field,
                            subField: 'mandatory',
                            updatedValue: false
                        }
                    })
                );
            }

        } else if (settingType === 'text') {

            const text = undefinedIfEmpty((e.target as HTMLInputElement).value.trim());
            const field = this.inputData.customization;
            const finalValue = text;

            this.dispatchEvent(
                new CustomEvent('on-toggle-subfield', {
                    detail: {
                        field,
                        subField: type,
                        updatedValue: finalValue
                    }
                })
            );

            logWithAmplitude(this.amplitude_callsite, field, { value: finalValue, type });
        }
    }

    private async openUpgradeDrawer() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.CustomizeAddPointForm,
                },
            })
        );
    }

    private async updateUrlSettings(settings: Partial<CustomFieldUrlType>) {
        this.dispatchEvent(
            new CustomEvent('update-url-settings', {
                detail: settings
            })
        );
    }

    private async updateRatingSettings(settings: Partial<CustomFieldRatingType>) {
        this.dispatchEvent(
            new CustomEvent('update-rating-settings', {
                detail: settings
            })
        );
    }

    private updateCrowdsourceSetting(category: TopicCategory, canCrowdsource: boolean) {
        this.dispatchEvent(
            new CustomEvent('update-crowdsource-setting', {
                detail: {
                    category,
                    canCrowdsource
                }
            })
        );
        // find category in this.categories and update it
        const index = this.categories.findIndex((item) => item._id === category._id);
        if (index !== -1) {
            this.categories[index].can_crowdsource = canCrowdsource;
        }

        // reorder categories so that the ones that can be crowdsourced are first
        this.categories.sort((a, b) => {
            if (a.can_crowdsource && !b.can_crowdsource) {
                return -1;
            } else if (!a.can_crowdsource && b.can_crowdsource) {
                return 1;
            } else {
                return 0;
            }
        });
        this.requestUpdate();
    }

    private updateTagCrowdsourceSetting(tag: UserTag, canCrowdsource: boolean) {
        this.dispatchEvent(
            new CustomEvent('update-tag-crowdsource-setting', {
                detail: {
                    tag,
                    canCrowdsource
                }
            })
        );
        // find category in this.tags and update it
        const index = this.tags.findIndex((item) => item._id === tag._id);
        if (index !== -1) {
            this.tags[index].can_crowdsource = canCrowdsource;
        }

        // reorder categories so that the ones that can be crowdsourced are first
        this.tags.sort((a, b) => {
            if ((a.can_crowdsource ?? true) && !(b.can_crowdsource ?? true)) {
                return -1;
            } else if (!(a.can_crowdsource ?? true) && (b.can_crowdsource ?? true)) {
                return 1;
            } else {
                return 0;
            }
        });
        this.requestUpdate();
    }

    private getEntitlementIcon() {
        // look up what level this featureType is at in types
        this.upgradeImg = '/images/tiers/starter_small.png';
    }

    render() {
        return html`
                    <div class="setting-section ${this.inputData.userHasEntitlement ? '' : 'disabled'}" @click=${this.inputData.userHasEntitlement ? '' : this.openUpgradeDrawer}>
                        ${this.inputData.hasHideOnPointCard ? html`
                            <div class="checkSection">
                                <h4 class="helpSubHeader">Show ${this.title} on ${topicData.topic?.point_name ?? 'Point'} Card to Viewers
                                    <form-tooltip
                                        tipwidth="180px"
                                        leftPadding="30%"
                                        message="Hiding this means admins will be able to see the ${this.title} when they edit but viewers will not.">
                                            <mwc-icon>info_outline</mwc-icon>
                                    </form-tooltip>
                                </h4>
                                <div class="setting-switch">
                                    <form-switch class="switch-icon" id="isCheckedShowOnCard" @change=${async (event: Event) => await this.settingIsChanged(event,'hide_on_point_card', 'toggle')} ?disabled=${!this.hasEntitlement}></form-switch>
                                </div>
                            </div>
                            ${!(this.isCheckedShowOnCardElement?.checked  ?? true) ? html`
                                <p class="description">This field is hidden on the ${topicData.topic?.point_name ?? 'Point'} card for people with the link, but you will still see it when logged in as an admin.</p>
                            ` : ''}
                        ` : ''}
                        <div class="checkSection">
                            ${this.hasEntitlement ? '' : html`
                                <img class="upgradeImg" src="${this.upgradeImg ?? ''}" />
                            `}
                            ${this.inputData.hasHideOnForm ? html`
                                <h4 class="helpSubHeader">Show ${this.title} on Add ${topicData.topic?.point_name ?? 'Point'} Form
                                    ${this.inputData.visibilityRules && this.inputData.visibilityRules.length > 0 && !this.is_hidden ? html`
                                        <form-tooltip
                                            tipwidth="180px"
                                            leftPadding="30%"
                                            message="Visibility in form may be impacted by logic rules below.">
                                                <mwc-icon>warning</mwc-icon>
                                        </form-tooltip>
                                    ` : ''}
                                </h4>
                                <div class="setting-switch" @click=${this.hasEntitlement ? '' : this.openUpgradeDrawer}>
                                    <form-switch class="switch-icon" id="isCheckedShowOnForm" @change=${async (event: Event) => await this.settingIsChanged(event,'hide','toggle')} ?disabled=${!this.hasEntitlement}></form-switch>
                                </div>
                            ` : html`
                                    <h4 class="helpSubHeader">${this.title}</h4>
                            `}

                        </div>
                    ${this.is_hidden ? '' : html`
                        <div class="helpSubHeader__subfield ${this.hasEntitlement ? '' : 'disabled'}" @click=${this.hasEntitlement ? '' : this.openUpgradeDrawer}>
                            ${this.inputData.hasRequired ? html`
                                <div class="checkSection">
                                    <h4 class="helpSubHeader">Make ${this.title} Required</h4>
                                    <div class="setting-switch">
                                        <form-switch class="switch-icon" id="isCheckedRequired" @change=${async (event: Event) => await this.settingIsChanged(event,'mandatory', 'toggle')} ?disabled=${!this.hasEntitlement}></form-switch>
                                    </div>
                                </div>
                            ` : ''}
                            ${this.inputData.hasTitle ? html`
                                <h4 class="helpSubHeader">${this.isCustomField ? `Custom ${this.title} Field` : `Rename ${this.title} Header`}</h4>
                                <mwc-textfield
                                    id="textElementTitle"
                                    style="width: 100%; margin-top: 0.5rem; color: grey; height: 60px; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                                    outlined
                                    value="${this.inputData.title ?? ''}"
                                    @change=${async (event: Event) => await this.settingIsChanged(event,'header', 'text')}
                                    charCounter='internal'
                                    ?disabled=${!this.hasEntitlement}
                                    maxlength="${70}"
                                ></mwc-textfield>
                            ` : ''}
                            ${this.inputData.hasDescription ? html`
                                <h4 class="helpSubHeader">Add Custom ${this.title} Instructions</h4>
                                <mwc-textfield
                                    id="textElementDescription"
                                    style="width: 100%; margin-top: 0.5rem; color: grey; height: 60px; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                                    outlined
                                    value="${this.inputData.description ?? ''}"
                                    @change=${async (event: Event) => await this.settingIsChanged(event,'sub_header', 'text')}
                                    charCounter='internal'
                                    ?disabled=${!this.hasEntitlement}
                                    maxlength="${1000}"
                                ></mwc-textfield>`
                            : ''}
                            ${this.inputData.customization === 'category' ? html`
                                <h4 class="helpSubHeader">Categories Available For Crowdsourcing
                                    <form-tooltip
                                        tipwidth="180px"
                                        leftPadding="30%"
                                        message="Limit which categories non-admins can add to.">
                                            <mwc-icon>info_outline</mwc-icon>
                                    </form-tooltip>
                                </h4>
                                <div class="category__wrapper">
                                    ${this.categories?.map((category) => {
                                        return html`
                                        <div class="category__item"
                                            style="${category.can_crowdsource ? `background-color: ${this.inputData.userHasEntitlement ? 'var(--main-primary-color)' : 'var(--main-moon-color)'}; color: white` : `background-color: #fff; border: 2px solid transparent;border: 1px solid var(--main-primary-color);`}"
                                            @click=${() => this.updateCrowdsourceSetting(category, !category.can_crowdsource)}>
                                            ${category.can_crowdsource ? html`<img class="menuPng" src="${category.icon}" />` : html`<mwc-icon style="margin:0">lock</mwc-icon>`}
                                            ${category.name}
                                            <div class="category__icon">${category.can_crowdsource ? String.fromCodePoint(0x2D) : String.fromCodePoint(0x2B)}</div>
                                        </div>
                                    `;})}
                                </div>
                            ` : ''}

                            ${this.inputData.customization === 'tags' ? html`
                                <h4 class="helpSubHeader">Tags Available For Crowdsourcing
                                    <form-tooltip
                                        tipwidth="180px"
                                        leftPadding="30%"
                                        message="Limit which tags non-admins can add to points.">
                                            <mwc-icon>info_outline</mwc-icon>
                                    </form-tooltip>
                                </h4>
                                <div class="category__wrapper">
                                    ${this.tags?.map((tag) => {
                                        return html`
                                        <div class="category__item"
                                            style="${tag.can_crowdsource !== false ? `background-color: ${this.inputData.userHasEntitlement ? tag.icon : 'var(--main-moon-color)'}; color: white` : `background-color: #fff; border: 2px solid transparent;border: 1px solid var(--main-primary-color);`}"
                                            @click=${() => this.updateTagCrowdsourceSetting(tag, !(tag.can_crowdsource ?? true))}>
                                            ${tag.can_crowdsource !== false ? html`<mwc-icon style="margin:0">check</mwc-icon>` : html`<mwc-icon style="margin:0">lock</mwc-icon>`}
                                            ${tag.name}
                                            <div class="category__icon">${tag.can_crowdsource !== false? String.fromCodePoint(0x2D) : String.fromCodePoint(0x2B)}</div>
                                        </div>
                                    `;})}
                                </div>
                            ` : ''}
                            ${this.inputData.customType && ('URL' in this.inputData.customType) ?
                                this.renderCustomButtonSettings(this.inputData)
                            : ''}
                            ${this.inputData.customType && ('Rating' in this.inputData.customType) ?
                                this.renderRatingSettings(this.inputData)
                            : ''}
                        </div>
                    `}
                </div>
            </div>

		`;
    }

    renderCustomButtonSettings(AddPointCustomizationComponent: AddPointCustomizationComponent) {
        if (!AddPointCustomizationComponent.customType || !('URL' in AddPointCustomizationComponent.customType)) {
            return html``;
        }
        return html`
            <div class="checkSection">
                <h4 class="helpSubHeader">Open Link in New Tab
                    <form-tooltip
                        tipwidth="180px"
                        leftPadding="30%"
                        message="When clicked, open this link in a new browser tab.">
                            <mwc-icon>info_outline</mwc-icon>
                    </form-tooltip>
                </h4>
                <div class="setting-switch">
                    <form-switch class="switch-icon" id="isCheckedOpenInNewTab" @change=${async (event: Event) => await this.updateUrlSettings({open_in_new_tab: (event as CustomEvent).detail})} ?disabled=${!this.hasEntitlement}></form-switch>
                </div>
            </div>
            <div class="checkSection">
                <h4 class="helpSubHeader">Show as Button
                    <form-tooltip
                        tipwidth="180px"
                        leftPadding="30%"
                        message="Display this field as a clickable button instead of a text link on the point cards.">
                            <mwc-icon>info_outline</mwc-icon>
                    </form-tooltip>
                </h4>
                <div class="setting-switch">
                    <form-switch class="switch-icon" id="isCheckedShowAsButton" @change=${async (event: Event) => await this.updateUrlSettings({show_as_button: (event as CustomEvent).detail})} ?disabled=${!this.hasEntitlement}></form-switch>
                </div>
            </div>
            ${this.isCheckedShowAsButtonElement?.checked  ?? false ? html`
                <div style="display: flex; flex-direction: row; align-items: center; margin-top: 1rem; gap: 1rem;">
                    <div style="display: flex; flex-direction:column; flex: 1;">
                        <h4 class="helpSubHeader" style="flex: 1;">Button Text</h4>
                        <mwc-textfield
                            id="textElementButtonName"
                            style="width: 100%; margin-top: 0.5rem; color: grey; height: 60px; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                            outlined
                            value="${AddPointCustomizationComponent.customType?.URL?.button_text ?? ''}"
                            @change=${async (event: Event) => await this.updateUrlSettings({button_text: undefinedIfEmpty((event.target as HTMLInputElement).value.trim())})}
                            charCounter='internal'
                            ?disabled=${!this.hasEntitlement}
                            maxlength="${50}"
                        ></mwc-textfield>
                    </div>
                    <div style="display: flex; flex-direction:column; flex: 1;">
                        <h4 class="helpSubHeader">Button Color (Hex Code)</h4>
                        <mwc-textfield
                            id="textElementButtonColor"
                            style="width: 100%; margin-top: 0.5rem; color: grey; height: 60px; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                            outlined
                            value="${AddPointCustomizationComponent.customType?.URL?.button_color ?? ''}"
                            @change=${async (event: Event) => await this.updateUrlSettings({button_color: undefinedIfEmpty((event.target as HTMLInputElement).value.trim())})}
                            charCounter='internal'
                            regexp="^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$"
                            ?disabled=${!this.hasEntitlement}
                            maxlength="${7}"
                        ></mwc-textfield>
                    </div>
                    <div style="display: flex; flex-direction:column; flex: 1;">
                        <h4 class="helpSubHeader">Button Icon URL</h4>
                        <mwc-textfield
                            id="textElementButtonIconUrl"
                            style="width: 100%; margin-top: 0.5rem; color: grey; height: 60px; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                            outlined
                            value="${AddPointCustomizationComponent.customType?.URL?.button_icon_url ?? ''}"
                            @change=${async (event: Event) => {
                                const url: string = (event.target as HTMLInputElement).value.trim();
                                if (url && !isValidImageUrl(url)) {
                                    getMainPage().errorToastElement?.showToastAndThrow('Please enter a valid image URL');
                                    return;
                                }
                                await this.updateUrlSettings({button_icon_url: undefinedIfEmpty(url)});
                            }}
                            charCounter='internal'
                            ?disabled=${!this.hasEntitlement}
                            regexp="^https?:\/\/.*\.(png|jpe?g|gif|svg|webp)(\?.*)?$"
                            maxlength="${200}"
                        ></mwc-textfield>
                    </div>
                </div>
                <div>
                    <span class="description">Button Preview:</span>
                        <form-button
                        size="sm"
                        .backgroundColor="${AddPointCustomizationComponent.customType?.URL?.button_color}"
                        class="actions__button"
                    >
                        ${isValidString(AddPointCustomizationComponent.customType?.URL?.button_icon_url)
                            ? html`<img
                                src="${AddPointCustomizationComponent.customType?.URL?.button_icon_url}"
                                class="action__icon--custom-button"
                                alt="Custom Button Icon"
                            />`
                            : ''}
                        <span>${AddPointCustomizationComponent.customType?.URL?.button_text}</span>
                    </form-button>
                </div>
            ` : ''}
        `;
    }

    renderRatingSettings(component: AddPointCustomizationComponent) {
        if (!component.customType || !('Rating' in component.customType)) {
            return html``;
        }
        return html`
            <div class="checkSection">
                <h4 class="helpSubHeader">Max Stars
                    <form-tooltip
                        tipwidth="180px"
                        leftPadding="30%"
                        message="The maximum number of stars a user can select (1–10).">
                            <mwc-icon>info_outline</mwc-icon>
                    </form-tooltip>
                </h4>
                <mwc-textfield
                    type="number"
                    style="width: 80px; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                    outlined
                    min="1"
                    max="10"
                    value="${component.customType?.Rating?.maxStars ?? 5}"
                    @change=${async (event: Event) => {
                        const val = Math.min(10, Math.max(1, parseInt((event.target as HTMLInputElement).value) || 5));
                        (event.target as HTMLInputElement).value = String(val);
                        await this.updateRatingSettings({ maxStars: val });
                    }}
                    ?disabled=${!this.hasEntitlement}
                ></mwc-textfield>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                width: 100%;
                --icon-background-color: var(--theme-white-color);
                --icon-inner-color: var(--main-primary-color);
            }

            .category__wrapper {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                margin-top: 0.5rem;
            }

            .category__item {
                position: relative;
                user-select: none;
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 3px 14px 3px 10px;
                border-radius: 1.25rem;
                margin-right: 6px;
                margin-bottom: 4px;
                font-size: 10px;
                border: 1px solid var(--main-primary-color);
            }

            .category__icon {
                border-radius: 999px;
                width: 1rem;
                height: 1rem;
                font-size: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                top: -5px;
                right: -5px;
                background: var(--theme-white-color);
                border: 1px solid var(--main-primary-color);
                color: var(--main-primary-color);
            }

            .disabled {
                background-color: var(--theme-border-color);
                color: var(--theme-input-color);
            }

            .upgradeImg {
                height: 0.7rem;
                margin-right: 0.25rem;
            }

            .setting-switch--disabled {
                opacity: 0.5;
                touch-action: none;
                background-color: var(--main-moon-color);
                color: var(--theme-input-color);
            }

            .setting-switch {
                display: grid;
                grid-auto-flow: column;
                grid-auto-columns: 1fr;
                align-items: center;
                justify-content: space-evenly;
            }

            .setting-section {
                padding-bottom: 1rem;
            }

            .description {
                color: var(--theme-input-color);
            }

            .setting-switch--disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            .switch-label {
                text-align: center;
            }

            .switch-icon {
                margin-left: auto;
                margin-right: auto;
            }

            .gatedField {
                display: flex;
                margin-top: 1rem;
            }

            .gatedSection {
                cursor: pointer;
            }

            .helpSubHeader {
                margin: 0px;
                font-weight: normal;
                font-size: 0.75rem;
                flex: 1;
                color: var(--theme-primary-color);
                display: inline-flex;
                border-bottom: 1px solid var(--theme-border-color);
            }

            .action__icon--custom-button {
                height: 14px;
                padding-right: 6px;
            }

            mwc-icon {
                color: var(--theme-primary-color);
                margin-left: 0.5rem;
                font-size: 16px;
                text-decoration: none;
            }

            .helpSubHeader__subfield {
                font-size: 0.65rem;
                padding-left: 1rem;
                width: calc(100% - 1rem);
                border-bottom: none;
            }

            .helpSubHeader__subfield h4 {
                font-size: 0.75rem;
                text-decoration: none;
                border-bottom: none;
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 15px;
                margin-top: auto;
            }

            .checkSection {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 0.5rem;
            }

            .color-picker {
                flex: 1;
            }

            @media only screen and (max-width: 550px) {
                .menuPng {
                    margin-top: 20px;
                }
            }
        `;
    }
}

